import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { companyAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Building2, Landmark, ShieldCheck, Settings2, ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';

const SetupWizard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [company, setCompany] = useState({
        name: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        phone: '',
        website: '',
        pan: '',
        tan: '',
        pf_number: '',
        esi_number: '',
        bank_name: '',
        bank_account_number: '',
        bank_ifsc: '',
        bank_branch: '',
        pt_state: '',
        current_financial_year: '2025-26',
    });

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const response = await companyAPI.getCurrent();
                setCompany(prev => ({ ...prev, ...response.data }));
            } catch (error) {
                console.error('Failed to fetch company:', error);
            }
        };
        fetchCompany();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCompany(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        setCompany(prev => ({ ...prev, [name]: value }));
    };

    const handleNext = () => setStep(prev => prev + 1);
    const handleBack = () => setStep(prev => prev - 1);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await companyAPI.updateCurrent({
                ...company,
                is_setup_complete: true,
            });
            toast.success('Setup completed successfully!');
            // Update local storage user data to reflect setup completion
            const storedUser = JSON.parse(localStorage.getItem('user'));
            if (storedUser) {
                storedUser.company_setup_complete = true;
                localStorage.setItem('user', JSON.stringify(storedUser));
            }
            // Redirect to dashboard
            const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
            window.location.href = `${basePath}/dashboard`;
        } catch (error) {
            console.error('Setup failed:', error);
            toast.error('Failed to complete setup. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        { title: 'Company Details', icon: Building2 },
        { title: 'Statutory Info', icon: ShieldCheck },
        { title: 'Bank Details', icon: Landmark },
        { title: 'Finalize', icon: Settings2 },
    ];

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-2xl mb-8">
                <div className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-2">
                        <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/20">
                            <Building2 className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Setup Assistant</h1>
                            <p className="text-sm text-slate-500 font-medium">Configure your workspace to get started</p>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={logout} className="text-slate-500 hover:text-slate-900">
                        Sign Out
                    </Button>
                </div>

                {/* Progress bar */}
                <div className="relative mb-12">
                    <div className="absolute top-1/2 left-0 right-0 h-1 bg-slate-200 -translate-y-1/2 z-0 rounded-full" />
                    <div
                        className="absolute top-1/2 left-0 h-1 bg-primary -translate-y-1/2 z-0 transition-all duration-500 rounded-full"
                        style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%` }}
                    />
                    <div className="relative z-10 flex justify-between">
                        {steps.map((s, i) => (
                            <div key={i} className="flex flex-col items-center">
                                <div
                                    className={`h-10 w-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${step > i + 1
                                        ? 'bg-primary border-primary text-primary-foreground'
                                        : step === i + 1
                                            ? 'bg-white border-primary text-primary shadow-md scale-110'
                                            : 'bg-white border-slate-200 text-slate-400'
                                        }`}
                                >
                                    {step > i + 1 ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                                </div>
                                <span className={`text-[10px] mt-2 font-semibold uppercase tracking-wider ${step === i + 1 ? 'text-primary' : 'text-slate-400'
                                    }`}>
                                    {s.title}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <Card className="border-none shadow-xl shadow-slate-200/50 overflow-hidden">
                    <CardHeader className="bg-white border-b border-slate-100 pb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/5 text-primary">
                                {React.createElement(steps[step - 1].icon, { className: "h-5 w-5" })}
                            </div>
                            <div>
                                <CardTitle className="text-xl">{steps[step - 1].title}</CardTitle>
                                <CardDescription>Step {step} of 4</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-8">
                        {step === 1 && (
                            <div className="grid grid-cols-2 gap-6">
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="name">Company Name</Label>
                                    <Input id="name" name="name" value={company.name} onChange={handleChange} placeholder="Orbit Solutions Pvt Ltd" />
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="address">Registered Address</Label>
                                    <Input id="address" name="address" value={company.address} onChange={handleChange} placeholder="Street, Building, etc." />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="city">City</Label>
                                    <Input id="city" name="city" value={company.city} onChange={handleChange} placeholder="Bangalore" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="state">State</Label>
                                    <Input id="state" name="state" value={company.state} onChange={handleChange} placeholder="Karnataka" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="pincode">Pincode</Label>
                                    <Input id="pincode" name="pincode" value={company.pincode} onChange={handleChange} placeholder="560001" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone Number</Label>
                                    <Input id="phone" name="phone" value={company.phone} onChange={handleChange} placeholder="+91 9876543210" />
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="website">Website URL</Label>
                                    <Input id="website" name="website" value={company.website} onChange={handleChange} placeholder="https://example.com" />
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="pan">PAN Card Number</Label>
                                    <Input id="pan" name="pan" value={company.pan} onChange={handleChange} placeholder="ABCDE1234F" className="uppercase" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tan">TAN Number</Label>
                                    <Input id="tan" name="tan" value={company.tan} onChange={handleChange} placeholder="BLRR12345C" className="uppercase" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="pf_number">PF Registration Number</Label>
                                    <Input id="pf_number" name="pf_number" value={company.pf_number} onChange={handleChange} placeholder="KN/BLR/12345/678" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="esi_number">ESI Registration Number</Label>
                                    <Input id="esi_number" name="esi_number" value={company.esi_number} onChange={handleChange} placeholder="31001234560001001" />
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="pt_state">PT Registered State</Label>
                                    <Select value={company.pt_state} onValueChange={(v) => handleSelectChange('pt_state', v)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select PT State" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="KARNATAKA">Karnataka</SelectItem>
                                            <SelectItem value="MAHARASHTRA">Maharashtra</SelectItem>
                                            <SelectItem value="TAMIL_NADU">Tamil Nadu</SelectItem>
                                            <SelectItem value="TELANGANA">Telangana</SelectItem>
                                            <SelectItem value="DELHI">Delhi</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="grid grid-cols-2 gap-6">
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="bank_name">Primary Bank Name</Label>
                                    <Input id="bank_name" name="bank_name" value={company.bank_name} onChange={handleChange} placeholder="HDFC Bank" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="bank_account_number">Bank Account Number</Label>
                                    <Input id="bank_account_number" name="bank_account_number" value={company.bank_account_number} onChange={handleChange} placeholder="50100234567890" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="bank_ifsc">IFSC Code</Label>
                                    <Input id="bank_ifsc" name="bank_ifsc" value={company.bank_ifsc} onChange={handleChange} placeholder="HDFC0001234" className="uppercase" />
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="bank_branch">Branch Address</Label>
                                    <Input id="bank_branch" name="bank_branch" value={company.bank_branch} onChange={handleChange} placeholder="Indiranagar Branch, Bangalore" />
                                </div>
                            </div>
                        )}

                        {step === 4 && (
                            <div className="space-y-6">
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-6 space-y-4">
                                    <h3 className="font-semibold text-slate-900 border-b border-white pb-2 flex items-center gap-2">
                                        <Check className="h-4 w-4 text-green-500" />
                                        Setup Summary
                                    </h3>
                                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
                                        <div>
                                            <span className="text-slate-500 block">Organization</span>
                                            <span className="font-medium">{company.name || 'Not set'}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 block">Financial Year</span>
                                            <span className="font-medium text-primary">{company.current_financial_year}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="current_financial_year">Default Financial Year</Label>
                                    <Select value={company.current_financial_year} onValueChange={(v) => handleSelectChange('current_financial_year', v)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select FY" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="2024-25">2024-25</SelectItem>
                                            <SelectItem value="2025-26">2025-26</SelectItem>
                                            <SelectItem value="2026-27">2026-27</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-slate-500">This will be used for payroll and tax calculations.</p>
                                </div>

                                <div className="p-4 rounded-lg bg-blue-50 border border-blue-100 flex gap-3">
                                    <div className="h-6 w-6 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center text-white">
                                        <span className="text-xs font-bold">i</span>
                                    </div>
                                    <p className="text-sm text-blue-700 leading-relaxed">
                                        By finishing the setup, you agree to the default configurations. You can always change these settings later in the settings panel.
                                    </p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                    <CardFooter className="bg-slate-50 border-t border-slate-100 p-6 flex justify-between">
                        {step > 1 ? (
                            <Button variant="outline" onClick={handleBack} disabled={loading} className="gap-2">
                                <ArrowLeft className="h-4 w-4" /> Previous
                            </Button>
                        ) : (
                            <div />
                        )}

                        {step < 4 ? (
                            <Button onClick={handleNext} className="gap-2 shadow-lg shadow-primary/20">
                                Next Step <ArrowRight className="h-4 w-4" />
                            </Button>
                        ) : (
                            <Button onClick={handleSubmit} disabled={loading} className="gap-2 shadow-lg shadow-primary/20 min-w-[140px]">
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" /> Finalizing...
                                    </>
                                ) : (
                                    <>
                                        Complete Setup <Check className="h-4 w-4" />
                                    </>
                                )}
                            </Button>
                        )}
                    </CardFooter>
                </Card>
            </div>
        </div>
    );
};

export default SetupWizard;
